import "./ClassDetailsPanel.css";
import React, { useMemo, useState } from "react";
import { IRpgClass } from "../../models/IRpgClass";
import { getAbilityDisplayName } from "../../pages/Characters/characterHelpers";

export interface IClassRequirementCheck {
    valid: boolean;
    reasons: string[];
}

export interface IClassDetailsPanelProps {
    rpgClass: IRpgClass;
    imgSrc?: string;
    requirementCheck: IClassRequirementCheck;
}

const ClassDetailsPanel: React.FC<IClassDetailsPanelProps> = ({ rpgClass, imgSrc, requirementCheck }) => {
    const [hasImageError, setHasImageError] = useState(false);

    const resolvedImgSrc = useMemo(() => {
        if (!imgSrc || imgSrc.trim().length === 0) {
            return "";
        }

        return imgSrc.trim();
    }, [imgSrc]);

    const primaryStatsText = useMemo(() => {
        if (!rpgClass.PrimaryStats || rpgClass.PrimaryStats.length === 0) {
            return "";
        }

        return rpgClass.PrimaryStats.map((stat) => getAbilityDisplayName(stat)).join(", ");
    }, [rpgClass.PrimaryStats]);

    const showReqs = !requirementCheck.valid && requirementCheck.reasons.length > 0;

    const hasKeySkills = !!rpgClass.KeySkills && rpgClass.KeySkills.length > 0;
    const hasTraits = !!rpgClass.Traits && rpgClass.Traits.length > 0;

    return (
        <div className="class-details">
            <div className="class-details__header">
                {/* Optional image. If missing or errors, show NOTHING (no placeholder). */}
                {!hasImageError && resolvedImgSrc ? (
                    <div className="class-details__thumb">
                        <img
                            className="class-details__img"
                            src={resolvedImgSrc}
                            alt={rpgClass.Name}
                            onError={() => setHasImageError(true)}
                        />
                    </div>
                ) : null}

                <div className="class-details__header-text">
                    <h2 className="class-details__title">{rpgClass.Name}</h2>
                    <div className="class-details__desc">
                        <p>{rpgClass.LongDescription || rpgClass.Description}</p>
                    </div>
                </div>
            </div>

            {showReqs ? (
                <div className="class-details__reqs">
                    <div className="class-details__reqs-title">Requirements not met</div>
                    <ul className="class-details__reqs-list">
                        {requirementCheck.reasons.map((req) => (
                            <li key={req}>{req}</li>
                        ))}
                    </ul>
                    <div className="class-details__reqs-hint">
                        Adjust your Ability Scores (reroll or adjust point buy) to meet the requirements.
                    </div>
                </div>
            ) : null}

            <div className="class-details__section">
                <div className="class-details__section-title">Class Info</div>

                <ul className="class-details__kv">
                    <li><b>Hit Dice:</b> {rpgClass.HitDice}</li>

                    {primaryStatsText ? (
                        <li><b>Primary Attributes:</b> {primaryStatsText}</li>
                    ) : null}

                    {rpgClass.Role ? (
                        <li><b>Role:</b> {rpgClass.Role}</li>
                    ) : null}

                    {hasKeySkills ? (
                        <li><b>Key Skills:</b> {rpgClass.KeySkills!.join(", ")}</li>
                    ) : null}
                </ul>
            </div>

            {hasTraits ? (
                <div className="class-details__section">
                    <div className="class-details__section-title">Features</div>
                    <ul className="class-details__list">
                        {rpgClass.Traits!.map((trait) => (
                            <li key={trait.Name}>
                                <b>{trait.Name}:</b> {trait.Description}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export default ClassDetailsPanel;
